import React, { useState, useEffect } from 'react';
import { DepartmentNav } from '../components/DepartmentNav';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { SEO } from '../components/SEO';
import { Leaf, Calendar, Drop, Thermometer, CheckCircle, Warning, Clock, ArrowRight, Plant, Scissors, Play } from '@phosphor-icons/react';
import axios from 'axios';
import { API_URL } from '../config/api';

// 30-Day Overseeding Timeline based on University of Arizona AZ1683
const TIMELINE_STEPS = [
  {
    day: 'Day 1-3',
    phase: 'Scalp & Prep',
    title: 'Scalp Bermuda Low',
    description: 'Mow bermuda down to 0.5-0.75 inches. Remove all clippings. The goal is to open the canopy so ryegrass seed reaches the soil surface.',
    tips: ['Bag all clippings — don\'t mulch', 'Use a rotary mower on lowest setting', 'It should look bare and brown — that\'s the point'],
    icon: Scissors,
    color: 'text-amber-600',
    bgColor: 'bg-amber-50 dark:bg-amber-950/20',
    borderColor: 'border-amber-200 dark:border-amber-800',
  },
  {
    day: 'Day 3-5',
    phase: 'Scalp & Prep',
    title: 'Dethatch & Aerate',
    description: 'Verticut or dethatch to remove the thatch layer. Core aerate if compaction is an issue. This creates seed-to-soil contact — the most critical factor for germination.',
    tips: ['Rent a power dethatcher for best results', 'Core aeration holes = perfect seed pockets', 'Skip this step and germination drops 40-60%'],
    icon: Plant,
    color: 'text-orange-600',
    bgColor: 'bg-orange-50 dark:bg-orange-950/20',
    borderColor: 'border-orange-200 dark:border-orange-800',
  },
  {
    day: 'Day 5-7',
    phase: 'Seed',
    title: 'Spread Perennial Ryegrass',
    description: 'Broadcast perennial ryegrass seed at 10-15 lbs per 1,000 sq ft. Use a rotary spreader for even coverage. Make two passes at half rate in perpendicular directions.',
    tips: ['Perennial rye — NOT annual rye', '10 lbs/1K for light coverage, 15 lbs/1K for dense stand', 'Apply in morning before wind picks up (rye seed is light)'],
    icon: Leaf,
    color: 'text-green-600',
    bgColor: 'bg-green-50 dark:bg-green-950/20',
    borderColor: 'border-green-200 dark:border-green-800',
  },
  {
    day: 'Day 7-8',
    phase: 'Seed',
    title: 'Topdress (Optional)',
    description: 'Apply a thin layer (1/8 inch) of compost or peat moss over the seed. This improves seed-to-soil contact and retains moisture during germination.',
    tips: ['Don\'t bury the seed — ryegrass needs light to germinate', 'Peat moss works great but dries out fast', 'Compost adds nutrients and holds moisture better'],
    icon: Plant,
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-50 dark:bg-emerald-950/20',
    borderColor: 'border-emerald-200 dark:border-emerald-800',
  },
  {
    day: 'Day 7-21',
    phase: 'Water',
    title: 'Germination Watering',
    description: 'Keep the top 1 inch of soil consistently moist. Water 2-4 times daily for 5-10 minutes each. Ryegrass germinates in 5-10 days — you\'ll see sprouts by day 12-14.',
    tips: ['Morning, midday, afternoon, evening — short cycles', 'The seed must never dry out during germination', 'Reduce frequency once sprouts are 1 inch tall'],
    icon: Drop,
    color: 'text-blue-600',
    bgColor: 'bg-blue-50 dark:bg-blue-950/20',
    borderColor: 'border-blue-200 dark:border-blue-800',
  },
  {
    day: 'Day 21-28',
    phase: 'Establish',
    title: 'First Mow & Fertilize',
    description: 'Once ryegrass reaches 2-3 inches, mow at 1.5-2 inches. Apply a starter fertilizer (high phosphorus) to push root development. Transition from frequent light watering to deeper, less frequent irrigation.',
    tips: ['Never remove more than 1/3 of the blade in one mow', 'Starter fert like 18-24-12 works great', 'Switch to watering every other day, deeper soaks'],
    icon: Scissors,
    color: 'text-teal-600',
    bgColor: 'bg-teal-50 dark:bg-teal-950/20',
    borderColor: 'border-teal-200 dark:border-teal-800',
  },
  {
    day: 'Day 28-30',
    phase: 'Maintain',
    title: 'Enjoy Green Winter Lawn',
    description: 'Your bermuda is dormant underneath, but the perennial ryegrass gives you a lush green lawn through winter. Maintain mowing at 1.5-2 inches and fertilize monthly with nitrogen.',
    tips: ['Apply 0.5 lb nitrogen per 1,000 sqft monthly through winter', 'Continue watering — rye needs moisture even in cool weather', 'In spring, the bermuda will wake up and naturally outcompete the rye'],
    icon: CheckCircle,
    color: 'text-green-600',
    bgColor: 'bg-green-50 dark:bg-green-950/20',
    borderColor: 'border-green-200 dark:border-green-800',
  },
];

const SEED_RATE_TABLE = [
  { scenario: 'Light overseed (existing bermuda thin)', rate: '8-10 lbs', coverage: 'per 1,000 sqft' },
  { scenario: 'Standard overseed (full coverage)', rate: '10-12 lbs', coverage: 'per 1,000 sqft' },
  { scenario: 'Heavy overseed (bare areas, new lawn)', rate: '12-15 lbs', coverage: 'per 1,000 sqft' },
  { scenario: 'Athletic / high-traffic areas', rate: '15-20 lbs', coverage: 'per 1,000 sqft' },
];

function VideoCard({ title, description, videoId, creator }) {
  return (
    <div className="rounded-lg border overflow-hidden" data-testid={`fall-video-${videoId}`}>
      <div className="relative aspect-video bg-muted">
        <iframe
          src={`https://www.youtube.com/embed/${videoId}`}
          title={title}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          className="absolute inset-0 w-full h-full"
          loading="lazy"
        />
      </div>
      <div className="p-4">
        <h4 className="font-semibold text-sm">{title}</h4>
        {description && <p className="text-xs text-muted-foreground mt-1">{description}</p>}
        <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
          <Play size={12} weight="fill" /> {creator}
        </p>
      </div>
    </div>
  );
}

export default function FallPrepGuidePage() {
  const [videoGuides, setVideoGuides] = useState([]);
  const [videoSeason, setVideoSeason] = useState('');

  useEffect(() => {
    const fetchVideos = async () => {
      try {
        // Fetch fall-specific videos from all categories
        const [generalRes, weedRes, pestRes] = await Promise.all([
          axios.get(`${API_URL}/api/video-guides/general?season=fall`).catch(() => ({ data: { videos: [] } })),
          axios.get(`${API_URL}/api/video-guides/weed?season=fall`).catch(() => ({ data: { videos: [] } })),
          axios.get(`${API_URL}/api/video-guides/pest?season=fall`).catch(() => ({ data: { videos: [] } })),
        ]);
        const allVids = [
          ...(generalRes.data?.videos || []),
          ...(weedRes.data?.videos || []),
          ...(pestRes.data?.videos || []),
        ];
        // Deduplicate by youtube_video_id
        const seen = new Set();
        const unique = allVids.filter(v => {
          if (seen.has(v.youtube_video_id)) return false;
          seen.add(v.youtube_video_id);
          return true;
        });
        setVideoGuides(unique);
        setVideoSeason(generalRes.data?.season || weedRes.data?.season || '');
      } catch {
        console.error('Could not load video guides');
      }
    };
    fetchVideos();
  }, []);

  return (
    <div className="min-h-screen bg-background" data-testid="fall-prep-guide-page">
      <SEO
        title="Fall Overseeding Guide"
        description="Complete 30-day guide to overseeding bermudagrass with perennial ryegrass for a green winter lawn. Seed rates, timeline, and watering schedule based on University of Arizona research."
        keywords="fall overseeding, bermuda ryegrass overseed, winter lawn, perennial ryegrass, bermudagrass transition, overseeding timeline, ryegrass seed rate"
        path="/fall-guide"
        imageAlt="TurfPulse Fall Overseeding Guide — 30-day bermudagrass to perennial ryegrass timeline"
        article={{ publishedTime: '2026-04-08', author: 'TurfPulse', tags: ['fall lawn care', 'overseeding', 'bermuda', 'ryegrass'] }}
      />
      <DepartmentNav />

      <main className="max-w-5xl mx-auto p-4 sm:p-6 space-y-8">
        {/* Hero */}
        <div className="relative rounded-xl overflow-hidden bg-gradient-to-br from-amber-700 via-orange-800 to-red-900 p-8 sm:p-12 text-white" data-testid="fall-guide-hero">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-4 right-8 text-8xl">&#127810;</div>
            <div className="absolute bottom-4 left-12 text-6xl">&#127811;</div>
          </div>
          <div className="relative z-10">
            <div className="flex items-center gap-2 text-amber-200 text-sm font-medium mb-3 uppercase tracking-wider">
              <Leaf weight="duotone" size={18} />
              Fall Prep Guide
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 leading-tight">
              Overseed Bermuda with<br />Perennial Ryegrass
            </h1>
            <p className="text-lg text-amber-100 max-w-2xl">
              A 30-day science-backed plan for a green winter lawn. Based on the University of Arizona
              Cooperative Extension Guide (AZ1683) and adapted for Central Texas warm-season lawns.
            </p>
          </div>
        </div>

        {/* When to Start */}
        <Card data-testid="fall-when-to-start">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Thermometer weight="duotone" size={24} className="text-orange-500" />
              When to Start
            </CardTitle>
            <CardDescription>Timing is everything — don't overseed too early or too late</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid sm:grid-cols-3 gap-4">
              <div className="p-4 rounded-lg bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800">
                <div className="font-semibold text-blue-700 dark:text-blue-300 text-sm mb-1">Nighttime Temps</div>
                <div className="text-2xl font-bold text-foreground">Below 65&#176;F</div>
                <p className="text-xs text-muted-foreground mt-1">Consistently for 5+ nights</p>
              </div>
              <div className="p-4 rounded-lg bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800">
                <div className="font-semibold text-amber-700 dark:text-amber-300 text-sm mb-1">Soil Temperature</div>
                <div className="text-2xl font-bold text-foreground">Below 75&#176;F</div>
                <p className="text-xs text-muted-foreground mt-1">At 2-inch depth</p>
              </div>
              <div className="p-4 rounded-lg bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800">
                <div className="font-semibold text-green-700 dark:text-green-300 text-sm mb-1">Central Texas Timing</div>
                <div className="text-2xl font-bold text-foreground">Mid-Oct</div>
                <p className="text-xs text-muted-foreground mt-1">Typically Oct 10-25</p>
              </div>
            </div>

            <div className="bg-muted/50 rounded-lg p-4 border-l-4 border-amber-500">
              <p className="text-sm font-medium flex items-center gap-2">
                <Warning weight="duotone" size={18} className="text-amber-500 flex-shrink-0" />
                <span><strong>Too early?</strong> The bermuda competes with rye seed and wins. <strong>Too late?</strong> Cold temps slow germination and you get patchy coverage.</span>
              </p>
            </div>
          </CardContent>
        </Card>

        {/* 30-Day Timeline */}
        <div data-testid="fall-timeline">
          <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
            <Calendar weight="duotone" size={28} className="text-orange-500" />
            30-Day Overseeding Timeline
          </h2>

          <div className="space-y-4">
            {TIMELINE_STEPS.map((step, i) => {
              const Icon = step.icon;
              return (
                <Card key={i} className={`border-l-4 ${step.borderColor}`} data-testid={`timeline-step-${i}`}>
                  <CardContent className="pt-5 pb-5">
                    <div className="flex flex-col sm:flex-row gap-4">
                      <div className="flex-shrink-0">
                        <div className={`w-12 h-12 rounded-full ${step.bgColor} flex items-center justify-center`}>
                          <Icon weight="duotone" size={24} className={step.color} />
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground bg-muted px-2 py-0.5 rounded">
                            {step.day}
                          </span>
                          <span className={`text-xs font-medium px-2 py-0.5 rounded ${step.bgColor} ${step.color}`}>
                            {step.phase}
                          </span>
                        </div>
                        <h3 className="font-bold text-lg mb-2">{step.title}</h3>
                        <p className="text-muted-foreground text-sm mb-3">{step.description}</p>
                        <div className="space-y-1.5">
                          {step.tips.map((tip, j) => (
                            <div key={j} className="flex items-start gap-2 text-sm">
                              <ArrowRight size={14} className="text-muted-foreground flex-shrink-0 mt-0.5" />
                              <span className="text-muted-foreground">{tip}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Seed Rates */}
        <Card data-testid="fall-seed-rates">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Leaf weight="duotone" size={24} className="text-green-500" />
              Perennial Ryegrass Seed Rates
            </CardTitle>
            <CardDescription>How much seed you need depends on your coverage goal</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-2 font-semibold">Scenario</th>
                    <th className="text-center py-3 px-2 font-semibold">Seed Rate</th>
                    <th className="text-center py-3 px-2 font-semibold">Coverage</th>
                  </tr>
                </thead>
                <tbody>
                  {SEED_RATE_TABLE.map((row, i) => (
                    <tr key={i} className="border-b border-border/50 hover:bg-muted/30 transition-colors">
                      <td className="py-3 px-2">{row.scenario}</td>
                      <td className="py-3 px-2 text-center font-bold text-green-600 dark:text-green-400">{row.rate}</td>
                      <td className="py-3 px-2 text-center text-muted-foreground">{row.coverage}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="bg-muted/50 rounded-lg p-4 mt-4 text-sm">
              <p className="font-medium mb-1">Quick Math Example:</p>
              <p className="text-muted-foreground">
                5,000 sqft lawn &#215; 12 lbs/1,000 sqft = <strong>60 lbs of perennial ryegrass seed</strong>
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Critical Warnings */}
        <Card className="border-red-200 dark:border-red-800" data-testid="fall-warnings">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-600 dark:text-red-400">
              <Warning weight="duotone" size={24} />
              What NOT to Do
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid sm:grid-cols-2 gap-3">
              {[
                { title: 'Don\'t apply pre-emergent', desc: 'Pre-emergent herbicide prevents ALL seeds from germinating — including your ryegrass.' },
                { title: 'Don\'t skip the scalp', desc: 'Seed sitting on top of thick bermuda canopy won\'t reach the soil and won\'t germinate.' },
                { title: 'Don\'t use annual ryegrass', desc: 'Annual rye is cheaper but clumpy, coarse, and dies off unevenly. Perennial rye is worth the cost.' },
                { title: 'Don\'t let seed dry out', desc: 'One missed watering during days 7-14 can kill the entire germination cycle.' },
              ].map((item, i) => (
                <div key={i} className="flex gap-3 p-3 rounded-lg bg-red-50 dark:bg-red-950/20">
                  <Warning weight="fill" size={18} className="text-red-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-sm">{item.title}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Spring Transition */}
        <Card data-testid="fall-spring-transition">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock weight="duotone" size={24} className="text-purple-500" />
              Spring Transition Back to Bermuda
            </CardTitle>
            <CardDescription>What happens when bermuda wakes up in April/May</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-muted-foreground">
              In Central Texas, bermuda starts breaking dormancy in mid-April when soil temps hit 65&#176;F+.
              The ryegrass will naturally thin out as temps rise. To speed the transition:
            </p>
            <div className="grid sm:grid-cols-3 gap-3">
              <div className="p-3 rounded-lg bg-muted/50 border">
                <p className="font-semibold text-sm mb-1">Option 1: Let Nature Work</p>
                <p className="text-xs text-muted-foreground">Stop fertilizing rye in March. Bermuda will outcompete it by May. Easiest approach.</p>
              </div>
              <div className="p-3 rounded-lg bg-muted/50 border">
                <p className="font-semibold text-sm mb-1">Option 2: Scalp It Out</p>
                <p className="text-xs text-muted-foreground">Mow very low (0.5 inch) in late April to stress rye and give bermuda sunlight. Aggressive but effective.</p>
              </div>
              <div className="p-3 rounded-lg bg-muted/50 border">
                <p className="font-semibold text-sm mb-1">Option 3: Chemical Removal</p>
                <p className="text-xs text-muted-foreground">Use Katana or Revolver herbicide to selectively remove rye without harming bermuda. Pro-level approach.</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Video Guides */}
        {videoGuides.length > 0 && (
          <div data-testid="fall-video-guides">
            <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
              <Play weight="duotone" size={28} className="text-red-500" />
              Fall Video Guides
              {videoSeason && (
                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300 capitalize">
                  {videoSeason} picks
                </span>
              )}
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              {videoGuides.map((video) => (
                <VideoCard
                  key={video.video_id || video.youtube_video_id}
                  title={video.title}
                  description={video.description}
                  videoId={video.youtube_video_id}
                  creator={video.creator}
                />
              ))}
            </div>
            <p className="text-xs text-muted-foreground mt-4">
              Videos rotate seasonally. Featuring content from Ron Henry, GCI Matt, and Lawn Insider.
            </p>
          </div>
        )}

        {/* Source Attribution */}
        <div className="text-center py-6 border-t">
          <p className="text-xs text-muted-foreground">
            Overseeding guidelines adapted from the University of Arizona Cooperative Extension Guide (AZ1683)
            and field experience in Central Texas (Zone 8b). Seed rates and timing may vary by region.
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            Always refer to your local extension office for region-specific recommendations.
          </p>
        </div>
      </main>
    </div>
  );
}
